import { Alert, AlertDescription, AlertTitle, Button, Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import {
  ChoiceSelect,
  NotPermitted,
  PageHeader,
  ReasonDialog,
  SPLIT_HOLD
} from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { US_STATES, useAuth, useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type {
  IdentifierCheckDecisionKind,
  IIdentifierCheck
} from '../../api/identifier-check-contracts';
import { identifierChecksApi } from '../../api/identifier-checks-api';
import { participantsApi } from '../../api/participants-api';
import type { IWorkItemAssignee } from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { useListReread } from '../../components/list-area';
import { CardLink, personHubPath } from '../../components/participant-link';
import {
  CHECK_DECISIONS,
  checkDecisionLabel,
  checkDecisionSays,
  checkNumberText,
  checkReasonLabel,
  NOT_PERMITTED_CHECK,
  registryAnswerText
} from '../../number-checks';
import { readNumberCheckKey } from '../../paths';
import { refusalMessage } from '../../refusal';
import { Fact, Missing, PaneSection } from '../compliance/work-item-pane';
import { NoteLine, unanswered, type ISaidNote } from '../person-hub/said';
import { CheckStatusChip } from './number-checks-page';

/**
 * One number check, in the queue's pane (SR-487 ruling 15) — `/team/number-checks/:checkKey`, or
 * `/team/tasks/checks/:checkKey` from My tasks (ruling 16), the list beside it: whose number it
 * is, why registration could not corroborate it, FINRA's latest answer, who works it, and Verify,
 * Reject or Replace — or, once decided, who decided, when and their note.
 *
 * **Read off the participant's card.** survey-api has no route for one check: it carries each
 * participant's checks, every status, on the card (`GET /admin/participants/{id}`), under the
 * queue's own `responses.browse`. So the address names the participant beside the check
 * (`numberCheckKey`), and the pane reads that card and finds the check on it. A key that names no
 * pair, a participant survey-api does not hold, and a check not on the card are one answer: "No
 * such check" — never a white screen.
 *
 * **Every write's answer is the check**, adopted at once; the card and the list beside it are
 * read again after it (`useListReread`): a decision closes the check and an assignee moves its
 * row. What says the check is not what the page read — 409 `check-closed` (someone decided it
 * first), a 404, an answer that never arrived — is said once at the top of the pane, with the
 * check read again; a refusal the person can act on in place — another participant holds the
 * number (`identifier-taken`), a value or a state the type refuses — is said beside the control
 * that made it, with what was typed kept.
 *
 * Writes are `responses.annotate`, drawn disabled with `NotPermitted` for a reader without it. A
 * check is assigned only to an account that can work it — the work items' assignees, which
 * survey-api's own rule (`IsAssignableAsync`) is.
 */
export function AdminNumberCheckPage({
  crumb = 'Number checks'
}: {
  /** The list the check is read from — the queue's, or My tasks'. */
  crumb?: string;
}) {
  const { checkKey = '' } = useParams();
  // Keyed by the check: the route's element is not remounted when the list opens another one.
  return <CheckPane key={checkKey} checkKey={checkKey} crumb={crumb} />;
}

/** The read's answer — `found` null for no such check. */
interface IRead {
  found: Nullable<IIdentifierCheck>;
}

function CheckPane({ checkKey, crumb }: { checkKey: string; crumb: string }) {
  const rereadList = useListReread();
  const { data, error, reload, adopt } = useAdminData<IRead>(
    async (signal) => {
      const pair = readNumberCheckKey(checkKey);
      if (pair === null) return { found: null };
      const card = await participantsApi.getParticipant(pair.participantId, signal);
      return { found: card?.checks?.find((check) => check.checkId === pair.checkId) ?? null };
    },
    [checkKey]
  );
  const assignees = useAdminData<IWorkItemAssignee[]>(
    (signal) => workItemsApi.workItemAssignees(signal),
    []
  );
  const [notice, setNotice] = React.useState<Nullable<ISaidNote>>(null);

  const readAgain = React.useCallback(async () => {
    rereadList();
    return reload();
  }, [reload, rereadList]);

  if (data === null || data.found === null) {
    return (
      <>
        <PageHeader title="Number check" crumb={crumb} />
        <div className="grid gap-6 px-6 py-6">
          {data === null ? (
            error ? (
              <LoadError message={error} />
            ) : (
              <p className="text-sm text-muted-foreground">Reading the check…</p>
            )
          ) : (
            <Alert>
              <AlertTitle>No such check</AlertTitle>
              <AlertDescription>
                No number check has this address — it may have been removed from the page you came
                from.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </>
    );
  }

  const check = data.found;

  /** A write landed: its answer drawn at once, then the card and the list read again. */
  const onLanded = (answer: IIdentifierCheck) => {
    setNotice(null);
    adopt({ found: answer });
    void readAgain();
  };

  /** A write failed: what says the check moved under the page is the pane's to say, with the
   *  check read again (`null`); the rest is the control's sentence. */
  const onTrouble = (failure: unknown): Nullable<string> => {
    const sayAndReread = (title: string, text: string) => {
      setNotice({ tone: 'refused', title, text });
      void readAgain();
      return null;
    };
    if (failure instanceof ApiError && failure.isProblem(ProblemType.checkClosed)) {
      return sayAndReread(
        'Already decided',
        'Someone decided this check first; nothing was saved. It has been read again.'
      );
    }
    if (failure instanceof ApiError && failure.status === 404) {
      return sayAndReread(
        'Not done',
        'This check no longer exists — it has been read again.'
      );
    }
    if (unanswered(failure)) {
      return sayAndReread(
        'No answer',
        'The answer did not arrive — the check has been read again: check whether it changed ' +
          'before trying again.'
      );
    }
    return checkRefusal(failure);
  };

  return (
    <>
      <PageHeader
        title={checkNumberText(check)}
        crumb={crumb}
        meta={<CheckStatusChip status={check.status} />}
      />
      <div className="grid gap-6 px-6 py-6">
        {error && (
          <LoadError message={`Could not refresh — showing the check as last read. ${error}`} />
        )}
        {notice && <NoteLine note={notice} />}

        <Facts check={check} />

        <PaneSection title="Decision">
          {check.status === 'open' ? (
            <>
              <DecisionButtons check={check} onLanded={onLanded} onTrouble={onTrouble} />
              <AssigneeControl
                check={check}
                assignees={assignees.error ? null : assignees.data}
                assigneesError={assignees.error}
                onLanded={onLanded}
                onTrouble={onTrouble}
              />
            </>
          ) : (
            <Decided check={check} />
          )}
        </PaneSection>
      </div>
    </>
  );
}

/** Whose number it is, why it waits, who registered it and what FINRA last said. */
function Facts({ check }: { check: IIdentifierCheck }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      <Fact label="Participant">
        <CardLink to={personHubPath(check.participant.participantId)}>
          {check.participant.displayName}
        </CardLink>
      </Fact>
      <Fact label="Reason">{checkReasonLabel(check.reason)}</Fact>
      <Fact label="Raised">{formatDateTime(check.createdAt)}</Fact>
      <Fact label="Registered by">{check.account?.email ?? <Missing />}</Fact>
      <Fact label="Registration run">
        {check.runId ? (
          <span className="font-mono text-xs select-all">{check.runId}</span>
        ) : (
          <Missing />
        )}
      </Fact>
      <Fact label="Registry">
        {check.registry ? (
          <>
            {registryAnswerText(check.registry)}
            <span className="block text-xs text-muted-foreground">
              Answered {formatDateTime(check.registry.fetchedAt)}
            </span>
          </>
        ) : check.idType === 'CRD' ? (
          'No answer from FINRA BrokerCheck yet.'
        ) : (
          'No registry is asked about this number.'
        )}
      </Fact>
    </dl>
  );
}

/** A closed check: how it was decided, by whom, when, and their note. */
function Decided({ check }: { check: IIdentifierCheck }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      <Fact label="Decided by">{check.decidedBy?.email ?? <Missing />}</Fact>
      <Fact label="Decided">
        {check.decidedAt ? formatDateTime(check.decidedAt) : <Missing />}
      </Fact>
      <Fact label="Assigned to">{check.assignee?.email ?? 'Unassigned'}</Fact>
      <Fact label="Note">
        {check.note ? <span className="whitespace-pre-wrap">{check.note}</span> : <Missing />}
      </Fact>
    </dl>
  );
}

/** What the pane hands a control. */
interface ICheckWrites {
  check: IIdentifierCheck;
  /** A write landed, answering the check. */
  onLanded: (answer: IIdentifierCheck) => void;
  /** A write failed: `null` where the pane said it, else the sentence to say at the control. */
  onTrouble: (failure: unknown) => Nullable<string>;
}

/** The body members survey-api's refusals point at, as the controls here name them. */
const TARGET_LABEL: Record<string, string> = {
  decision: 'Decision',
  value: 'Number',
  issuingState: 'State',
  note: 'Note',
  accountId: 'Assigned to'
};

/** A refusal the person can act on, in the controls' words — or survey-api's own sentence. */
function checkRefusal(failure: unknown): string {
  if (failure instanceof ApiError) {
    if (failure.isProblem(ProblemType.identifierTaken)) {
      return 'Another participant holds that number, so nothing was saved.';
    }
    const problems = failure.problem?.problems ?? [];
    if (problems.length > 0) {
      return problems
        .map((problem) => `${TARGET_LABEL[problem.target] ?? problem.target}: ${problem.reason}`)
        .join('; ');
    }
  }
  return refusalMessage(failure, 'The decision was refused');
}

/** Verify, Reject and Replace, each confirmed in a dialog with an optional note. */
function DecisionButtons({ check, onLanded, onTrouble }: ICheckWrites) {
  const [open, setOpen] = React.useState<Nullable<IdentifierCheckDecisionKind>>(null);
  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();
  const licence = check.idType === 'INSURANCE_LICENSE';
  const [value, setValue] = React.useState('');
  const [state, setState] = React.useState(check.issuingState ?? '');
  // Each opening of Replace starts afresh: a value belongs to the press it was typed for.
  React.useEffect(() => {
    if (open === null) {
      setValue('');
      setState(check.issuingState ?? '');
    }
  }, [open, check.issuingState]);

  const decide = async (note: string): Promise<Nullable<string>> => {
    if (open === null) return null;
    let answer: IIdentifierCheck;
    try {
      answer = await identifierChecksApi.decide(check.checkId, {
        decision: open,
        ...(note !== '' && { note }),
        ...(open === 'replace' && { value: value.trim() }),
        ...(open === 'replace' && licence && { issuingState: state })
      });
    } catch (failure) {
      return onTrouble(failure);
    }
    onLanded(answer);
    return null;
  };

  const name = check.participant.displayName;
  return (
    <div className="grid gap-1">
      <div className="flex flex-wrap gap-2">
        {CHECK_DECISIONS.map((decision) => (
          <Button
            key={decision.value}
            type="button"
            size="sm"
            variant={decision.value === 'verify' ? 'default' : 'outline'}
            disabled={!permitted}
            aria-describedby={permitted ? undefined : reasonId}
            onClick={() => setOpen(decision.value)}
          >
            {decision.label}
          </Button>
        ))}
      </div>
      {!permitted && <NotPermitted id={reasonId} what={NOT_PERMITTED_CHECK} />}
      <ReasonDialog
        open={open !== null}
        onOpenChange={(next) => !next && setOpen(null)}
        title={open === null ? '' : `${checkDecisionLabel(open)}: ${checkNumberText(check)}`}
        description={open === null ? '' : checkDecisionSays(open, name)}
        reasonLabel="Note"
        required={false}
        confirmLabel={open === null ? 'Decide' : checkDecisionLabel(open)}
        incomplete={open === 'replace' && (value.trim() === '' || (licence && state === ''))}
        onConfirm={decide}
      >
        {open === 'replace' && (
          <ReplaceFields
            idType={check.idType}
            value={value}
            onValue={setValue}
            state={licence ? state : null}
            onState={setState}
          />
        )}
      </ReasonDialog>
    </div>
  );
}

/** A State that names nothing yet — the select's first choice. */
const STATE_CHOICES = [
  { value: '', label: 'Choose a state' },
  ...US_STATES.map(({ code, name }) => ({ value: code, label: name }))
];

/** Replace's number box, by the check's type. */
const RIGHT_NUMBER: Record<string, string> = {
  CRD: 'The right CRD',
  NPN: 'The right NPN',
  INSURANCE_LICENSE: 'The right licence number'
};

/** Replace's own fields, under the note: the right number, and a licence's state. */
function ReplaceFields({
  idType,
  value,
  onValue,
  state,
  onState
}: {
  idType: string;
  value: string;
  onValue: (value: string) => void;
  /** The licence's state; `null` for a number of any other type, which has none. */
  state: Nullable<string>;
  onState: (state: string) => void;
}) {
  const valueId = React.useId();
  const stateId = React.useId();
  return (
    <div className="grid gap-3 text-sm">
      <div className="grid gap-1">
        <label htmlFor={valueId} className="text-muted-foreground">
          {RIGHT_NUMBER[idType] ?? 'The right number'}
        </label>
        <Input
          id={valueId}
          value={value}
          autoComplete="off"
          onChange={(event) => onValue(event.target.value)}
        />
      </div>
      {state !== null && (
        <div className="grid gap-1">
          <span id={stateId} className="text-muted-foreground">
            State
          </span>
          <ChoiceSelect
            value={state}
            onChange={onState}
            choices={STATE_CHOICES}
            labelledBy={stateId}
          />
        </div>
      )}
    </div>
  );
}

/** survey-api's `null` assignee, as a value the kit's select can hold. */
const UNASSIGNED = '__unassigned__';

/**
 * Who works the check: one choice, written as it is made (`PUT …/assignee`), and "Assign to me"
 * for the viewer's own account — Compliance's control, without its revision: survey-api takes a
 * check's assignee without one. An assignee the list does not hold is still drawn, by address.
 */
function AssigneeControl({
  check,
  assignees,
  assigneesError,
  onLanded,
  onTrouble
}: ICheckWrites & {
  /** Whom the check can be handed to; `null` while the list is out or could not be read. */
  assignees: Nullable<IWorkItemAssignee[]>;
  assigneesError: Nullable<string>;
}) {
  const { user } = useAuth();
  const permitted = useCan()('responses.annotate');
  const [saving, setSaving] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const labelId = React.useId();
  const reasonId = React.useId();
  const current = check.assignee?.accountId ?? UNASSIGNED;
  const choices = [
    ...(assignees ?? []).map(({ accountId, email }) => ({ accountId, email })),
    ...(check.assignee && !assignees?.some((a) => a.accountId === check.assignee?.accountId)
      ? [check.assignee]
      : [])
  ];

  const assign = async (accountId: Nullable<string>) => {
    if (accountId === (check.assignee?.accountId ?? null)) return;
    setSaving(true);
    setRefusal(null);
    try {
      onLanded(await identifierChecksApi.assign(check.checkId, accountId));
    } catch (failure) {
      setRefusal(onTrouble(failure));
    } finally {
      setSaving(false);
    }
  };

  const mine = user !== null && check.assignee?.accountId !== user.accountId;
  return (
    <div className="grid gap-1 text-sm" {...(saving && SPLIT_HOLD)}>
      <span id={labelId} className="text-muted-foreground">
        Assigned to
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <ChoiceSelect
          value={current}
          onChange={(next) => void assign(next === UNASSIGNED ? null : next)}
          choices={[
            { value: UNASSIGNED, label: 'Unassigned' },
            ...choices.map((choice) => ({ value: choice.accountId, label: choice.email }))
          ]}
          disabled={saving || !permitted}
          labelledBy={labelId}
          className="w-72 max-w-full"
        />
        {mine && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={saving || !permitted}
            aria-describedby={permitted ? undefined : reasonId}
            onClick={() => void assign(user.accountId)}
          >
            Assign to me
          </Button>
        )}
        {saving && <span className="text-muted-foreground">Saving…</span>}
      </div>
      {!permitted && <NotPermitted id={reasonId} what={NOT_PERMITTED_CHECK} />}
      {assigneesError && (
        <span className="text-xs text-muted-foreground">
          The list of staff could not be read ({assigneesError}) — only Unassigned and you can be
          chosen.
        </span>
      )}
      {refusal && (
        <p role="alert" className="text-destructive">
          {refusal}
        </p>
      )}
    </div>
  );
}
